import { useState } from 'react'
import { useApi } from '../../../hooks/useApi'
import { RelocateApiService } from '../services/relocateApi'
import { ValidatePositionResponse, StockBySkuResponse } from '../types'
import { Batch } from '../../../interfaces/Batch'
import { toDunPacks } from '../utils/dunUtils'

interface UseOriginValidationProps {
  sharedState: {
    sku: string
    productOriginId: number | null
    hasBatchManagement?: boolean
    hasExpirationDate?: boolean
    posicionOrigen?: string
    isDun?: boolean
    dunQty?: number
  }
  setSharedState: (updater: (prev: any) => any) => void
}

interface UseOriginValidationReturn {
  localOriginPosition: string
  setLocalOriginPosition: (value: string) => void
  isValidating: boolean
  originValidated: boolean
  originValidationFailed: boolean
  error: string
  handleValidateOrigin: (scannedValue?: string) => Promise<void>
  handleEditOrigin: () => void
  stockItems: StockBySkuResponse[]
}

export const useOriginValidation = ({
  sharedState,
  setSharedState
}: UseOriginValidationProps): UseOriginValidationReturn => {
  // Inicializar con valores existentes de sharedState (para cuando se vuelve desde ConfirmationStep)
  const [localOriginPosition, setOriginPositionValue] = useState(sharedState.posicionOrigen || '')
  const [isValidating, setIsValidating] = useState(false)
  const [originValidated, setOriginValidated] = useState(Boolean(sharedState.posicionOrigen))
  const [originValidationFailed, setOriginValidationFailed] = useState(false)
  const [error, setError] = useState('')
  const [stockItems, setStockItems] = useState<StockBySkuResponse[]>([])

  const setLocalOriginPosition = (value: string) => {
    setOriginPositionValue(value)
    setError('')
    setOriginValidationFailed(false)
  }

  const { callApi: validatePositionCallApi } = useApi<ValidatePositionResponse>()
  const { callApi: stockCallApi } = useApi<StockBySkuResponse[]>()

  /**
   * Acepta el valor por parametro para el escaneo por camara: setLocalOriginPosition
   * es asincrono, asi que leer del estado en el mismo tick usaria el valor anterior.
   * Sin argumento se comporta igual que antes (Enter y boton Validar).
   */
  const handleValidateOrigin = async (scannedValue?: string) => {
    const originValue = (scannedValue ?? localOriginPosition).trim()
    if (!originValue) {
      setError('Ingrese una posición de origen válida')
      return
    }

    setIsValidating(true)
    setError('')
    setOriginValidationFailed(false)

    try {
      // Si la posición no es válida para reubicar (tipo incorrecto, BlockEgress, etc.)
      // el error debe cortar el flujo acá: antes se descartaba en silencio y se seguía
      // como si la posición fuera válida en tanto tuviera stock.
      const response = await validatePositionCallApi(RelocateApiService.validateOriginPosition(
        originValue,
        sharedState.sku,
        sharedState.productOriginId!
      ))

      const positionTypeId = response?.typePositionId
      let originPositionId = response?.positionId
      const originZone = response?.zone ?? null

      // Obtener el stock real de esa posición
      const stockResponse = await stockCallApi(RelocateApiService.getStockByPosition(
        originValue,
        sharedState.sku,
        sharedState.productOriginId!
      ))

      // Verificar si tenemos datos de stock
      if (!stockResponse || !Array.isArray(stockResponse)) {
        setError('Error al obtener datos de stock del servidor')
        setOriginValidationFailed(true)
        setIsValidating(false)
        return
      }

      // Obtener stock filtrado por posición
      const filteredStockItems = stockResponse.filter((item: StockBySkuResponse) =>
        item.positionCode?.toLowerCase().trim() === originValue.toLowerCase()
      )

      // Validar que existe stock en la posición
      if (filteredStockItems.length === 0) {
        const availablePositions = stockResponse
          .map(item => item.positionCode)
          .join(', ')
        setError(`No hay stock disponible en esta posición. Posiciones encontradas: ${availablePositions}`)
        setOriginValidationFailed(true)
        setIsValidating(false)
        return
      }

      // Si no obtuvimos datos de validación, usar los datos del primer stock item
      if (!originPositionId && filteredStockItems.length > 0) {
        originPositionId = filteredStockItems[0].positionId
      }

      // Calcular stock disponible total
      const availableStock = filteredStockItems.reduce((total, item) => total + (item.quantity || 0), 0)

      // Con DUN solo se mueven cajas completas: si el stock de la posición no
      // alcanza para una, no hay nada reubicable por este flujo. Se corta antes de
      // publicar los stockItems para no disparar el modal de lotes.
      if (sharedState.isDun && toDunPacks(availableStock, sharedState.dunQty) === 0) {
        setError(
          `La posición tiene ${availableStock} unidades y la unidad de medida del DUN es de ${sharedState.dunQty}: no alcanza para una unidad de medida completa`
        )
        setOriginValidationFailed(true)
        setIsValidating(false)
        return
      }

      // Actualizar stockItems para que el hook useBatchSelection lo procese
      setStockItems(filteredStockItems)

      // Preparar batches si existen (useBatchSelection determinará si se muestra el modal)
      const realBatches: Batch[] = filteredStockItems
        .filter(item => item.batch != null)
        .map((item) => ({
          batchId: item.batch!.batchId,
          batchNumber: item.batch!.batchNumber || '',
          expirationDate: item.batch!.expirationDate || '',
          quantity: item.quantity
        }))

      // Actualizar estado con todos los datos
      setSharedState(prev => ({
        ...prev,
        posicionOrigen: originValue,
        availableStock,
        originPositionId: originPositionId || filteredStockItems[0]?.positionId || 1,
        originPositionType: positionTypeId,
        originZone: originZone,
        stockKeepingUnitId: filteredStockItems[0]?.stockKeepingUnitId,
        availableBatches: realBatches,
        showBatchModal: false  // El componente principal decidirá si mostrar el modal
      }))

      setOriginValidated(true)

      // El hook useBatchSelection y el componente principal manejarán el modal
      // No llamamos onValidationSuccess aquí porque puede que se necesite abrir el modal primero

      // Reset merma state when validating origin
      setSharedState(prev => ({ ...prev, isDamaged: false }))
    } catch (err) {
      // callApi ya rechaza con el mensaje real del backend (string), formateado por
      // generateErrorFromResponse — no con el error de axios crudo.
      const errorMessage =
        typeof err === 'string'
          ? err
          : 'Posición de origen no válida o sin stock del producto indicado'
      setError(errorMessage)
      setOriginValidated(false)
      setOriginValidationFailed(true)
    } finally {
      setIsValidating(false)
    }
  }

  const handleEditOrigin = () => {
    setOriginValidated(false)
    setOriginValidationFailed(false)
    setSharedState(prev => ({
      ...prev,
      posicionOrigen: '',
      cantidad: null,
      posicionDestino: '',
      availableStock: undefined,
      originPositionId: undefined,
      destinyPositionId: undefined,
      isDamaged: false,
      originPositionType: undefined,
      destinyPositionType: undefined
    }))
    setError('')
  }

  return {
    localOriginPosition,
    setLocalOriginPosition,
    isValidating,
    originValidated,
    originValidationFailed,
    error,
    handleValidateOrigin,
    handleEditOrigin,
    stockItems
  }
}